import React, { useEffect, useState } from "react";
import {
  Container,
  Row,
  Col,
  Form,
  Card,
  Spinner,
} from "react-bootstrap";
import {
  Link,
  useNavigate,
  useParams,
} from "react-router-dom";
import { useDispatch } from "react-redux";

import {
  addroomType,
} from "../../store/slices/roomtype";

import {
  gettravelHotel,
} from "../../store/slices/travelhotel";

import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { FaPlus } from "react-icons/fa";

export default function AddRoomType() {
  const { id } = useParams();

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [hotel, setHotel] = useState(null);
  const [hotelLoading, setHotelLoading] = useState(true);

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const MAX_PRICE = 1000000;
  const MAX_OCCUPANCY = 8;
  const MAX_EXTRA_BEDS = 10;

  /* =========================
     INITIAL ROOM FORM
  ========================= */

  const initialRoomForm = {
    name: "",
    maxOccupancy: "",
    basePrice: "",
    extraBedAllowed: false,
    extraBedPrice: "",
    maxExtraBeds: "",
    childBedPrice: "",
    meals: {
      breakfast: {
        price: "",
        isIncluded: false,
      },
      lunch: {
        price: "",
        isIncluded: false,
      },
      dinner: {
        price: "",
        isIncluded: false,
      },
    },
  };

  const [roomForms, setRoomForms] = useState([
    initialRoomForm,
  ]);

  /* =========================
     FETCH SELECTED HOTEL
  ========================= */

  useEffect(() => {
    if (!id) {
      setHotelLoading(false);
      return;
    }

    fetchHotel();
  }, [id]);

  const fetchHotel = async () => {
    try {
      setHotelLoading(true);

      const response = await dispatch(
        gettravelHotel({
          id,
        })
      ).unwrap();

      console.log("Selected Hotel:", response);

      if (response?.hotel) {
        setHotel(response.hotel);
      } else {
        toast.error("Hotel not found");
      }

    } catch (error) {
      console.error("Failed to fetch hotel:", error);

      toast.error(
        error?.message ||
        "Failed to fetch hotel details"
      );

    } finally {
      setHotelLoading(false);
    }
  };

  /* =========================
     NUMBER ONLY
  ========================= */

  const allowOnlyNumbers = (value) =>
    value.replace(/[^0-9]/g, "");

  /* =========================
     LIMITED NUMBER CHANGE
  ========================= */

  const handleLimitedNumberChange = (
    index,
    name,
    value,
    maxLimit
  ) => {
    let numericValue = allowOnlyNumbers(value);

    if (Number(numericValue) > maxLimit) {
      numericValue = maxLimit.toString();

      setErrors((prev) => ({
        ...prev,
        [name]: `Maximum allowed value is ${maxLimit}`,
      }));
    } else {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }

    setRoomForms((prev) => {
      const updated = [...prev];

      updated[index] = {
        ...updated[index],
        [name]: numericValue,
      };

      return updated;
    });
  };

  /* =========================
     PRICE CHANGE
  ========================= */

  const handlePriceChange = (
    index,
    name,
    value,
    meal = null
  ) => {
    let numericValue = allowOnlyNumbers(value);

    if (
      name === "childBedPrice" &&
      numericValue === ""
    ) {
      setErrors((prev) => ({
        ...prev,
        childBedPrice:
          "Child extra bed price is required",
      }));
    }

    if (Number(numericValue) > MAX_PRICE) {
      numericValue = MAX_PRICE.toString();

      setErrors((prev) => ({
        ...prev,
        [name]:
          `Maximum allowed amount is ₹${MAX_PRICE}`,
      }));
    } else {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }

    setRoomForms((prev) => {
      const updated = [...prev];

      if (meal) {
        updated[index] = {
          ...updated[index],
          meals: {
            ...updated[index].meals,
            [meal]: {
              ...updated[index].meals[meal],
              price: numericValue,
            },
          },
        };
      } else {
        updated[index] = {
          ...updated[index],
          [name]: numericValue,
        };
      }

      return updated;
    });
  };

  /* =========================
     VALIDATION
  ========================= */

  const validateForm = () => {
    let newErrors = {};

    const form = roomForms[0];

    if (!form.name.trim()) {
      newErrors.name = "Please enter room type";
    }

    if (!form.basePrice) {
      newErrors.basePrice =
        "Please enter base price";
    } else if (
      isNaN(form.basePrice) ||
      Number(form.basePrice) <= 0
    ) {
      newErrors.basePrice =
        "Base price must be a valid amount";
    }

    if (!form.maxOccupancy) {
      newErrors.maxOccupancy =
        "Please enter max occupancy";
    } else if (
      isNaN(form.maxOccupancy) ||
      Number(form.maxOccupancy) <= 0 ||
      Number(form.maxOccupancy) > MAX_OCCUPANCY
    ) {
      newErrors.maxOccupancy =
        `Max occupancy must be between 1 and ${MAX_OCCUPANCY}`;
    }

    if (form.extraBedAllowed) {
      if (
        !form.maxExtraBeds ||
        isNaN(form.maxExtraBeds) ||
        Number(form.maxExtraBeds) > MAX_EXTRA_BEDS
      ) {
        newErrors.maxExtraBeds =
          `Max extra beds must be between 1 and ${MAX_EXTRA_BEDS}`;
      }
    }

    [
      "breakfast",
      "lunch",
      "dinner",
    ].forEach((meal) => {
      const price = form.meals[meal].price;

      if (price && isNaN(price)) {
        newErrors[meal] =
          "Meal price must be numeric";
      }
    });

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  /* =========================
     GENERAL CHANGE
  ========================= */

  const handleChange = (index, e) => {
    const {
      name,
      value,
      type,
      checked,
    } = e.target;

    setRoomForms((prev) => {
      const updatedForms = [...prev];

      updatedForms[index] = {
        ...updatedForms[index],
        [name]:
          type === "checkbox"
            ? checked
            : value,
      };

      return updatedForms;
    });

    /* LIVE VALIDATION */

    if (name === "name") {
      const nameRegex =
        /^[a-zA-Z0-9][\s\S]*$/;

      if (!value.trim()) {
        setErrors((prev) => ({
          ...prev,
          name: "Please enter room type",
        }));
      } else if (!nameRegex.test(value)) {
        setErrors((prev) => ({
          ...prev,
          name: "Please enter letters first",
        }));
      } else if (value.trim().length < 3) {
        setErrors((prev) => ({
          ...prev,
          name:
            "Minimum 3 characters required",
        }));
      } else if (value.trim().length > 50) {
        setErrors((prev) => ({
          ...prev,
          name:
            "Maximum 50 characters allowed",
        }));
      } else {
        setErrors((prev) => ({
          ...prev,
          name: "",
        }));
      }
    }
  };

  /* =========================
     MEAL CHANGE
  ========================= */

  const handleMealChange = (
    index,
    meal,
    field,
    value
  ) => {
    setRoomForms((prev) => {
      const updatedForms = [...prev];

      updatedForms[index] = {
        ...updatedForms[index],
        meals: {
          ...updatedForms[index].meals,
          [meal]: {
            ...updatedForms[index].meals[meal],
            [field]: value,
          },
        },
      };

      return updatedForms;
    });

    if (errors[meal]) {
      setErrors((prev) => ({
        ...prev,
        [meal]: "",
      }));
    }
  };

  /* =========================
     ADD ROOM TYPE
  ========================= */

  const handleAddRoomType = () => {
    if (roomForms.length >= 11) {
      toast.error(
        "Maximum 10 room types allowed"
      );
      return;
    }

    setRoomForms((prev) => [
      ...prev,
      {
        name: "",
        maxOccupancy: "",
        basePrice: "",
        extraBedAllowed: false,
        extraBedPrice: "",
        maxExtraBeds: "",
        childBedPrice: "",
        meals: {
          breakfast: {
            price: "",
            isIncluded: false,
          },
          lunch: {
            price: "",
            isIncluded: false,
          },
          dinner: {
            price: "",
            isIncluded: false,
          },
        },
      },
    ]);
  };

  /* =========================
     REMOVE ROOM TYPE
  ========================= */

  const handleRemoveRoomType = (index) => {
    const updated = [...roomForms];

    updated.splice(index, 1);

    setRoomForms(updated);
  };

  /* =========================
     SUBMIT
  ========================= */

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!id) {
      toast.error("Hotel ID is missing");
      return;
    }

    if (!hotel) {
      toast.error(
        "Hotel information is not available"
      );
      return;
    }

    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);

      for (const room of roomForms) {

        const payload = {

          /* IMPORTANT:
             This connects the room type
             to the selected hotel.
          */

          hotel: id,

          name: room.name.trim(),

          maxOccupancy:
            Number(room.maxOccupancy),

          basePrice:
            Number(room.basePrice),

          extraBedAllowed:
            room.extraBedAllowed,

          extraBedPrice:
            room.extraBedAllowed
              ? Number(room.extraBedPrice)
              : 0,

          maxExtraBeds:
            room.extraBedAllowed
              ? Number(room.maxExtraBeds)
              : 0,

          childwithBedPrice:
            Number(room.childBedPrice || 0),

          meals: {

            breakfast: {
              price:
                Number(
                  room.meals.breakfast.price || 0
                ),
              isIncluded:
                room.meals.breakfast.isIncluded,
            },

            lunch: {
              price:
                Number(
                  room.meals.lunch.price || 0
                ),
              isIncluded:
                room.meals.lunch.isIncluded,
            },

            dinner: {
              price:
                Number(
                  room.meals.dinner.price || 0
                ),
              isIncluded:
                room.meals.dinner.isIncluded,
            },

          },

        };

        console.log(
          "Adding Room Payload:",
          payload
        );

        await dispatch(
          addroomType(payload)
        ).unwrap();
      }

      toast.success(
        "Rooms added successfully!"
      );

      navigate(
        `/admin/hotel-rooms/${id}`
      );

    } catch (err) {

      console.error(err);

      toast.error(
        err?.message ||
        err ||
        "Failed to add rooms"
      );

    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* =========================
          BREADCRUMB
      ========================= */}

      <div className="breadcrumb-bar mb-2">

        <div className="breadcrumb-left">

          <Link
            to="/admin/home"
            className="breadcrumb-link active"
          >
            Home
          </Link>

          <span className="breadcrumb-separator">
            |
          </span>

          <Link
            to="/admin/roomtype"
            className="breadcrumb-link active"
          >
            Hotels
          </Link>

          <span className="breadcrumb-separator">
            |
          </span>

          <Link
            to={`/admin/hotel-rooms/${id}`}
            className="breadcrumb-link active"
          >
            {hotel?.name || "Hotel"}
          </Link>

          <span className="breadcrumb-separator">
            |
          </span>

          <span className="breadcrumb-link inactive">
            Add Room Type
          </span>

        </div>

      </div>

      <Container fluid className="p-4">

        {/* =========================
            HOTEL INFORMATION
        ========================= */}

        <Card
          className="mb-4 border-0"
          style={{
            borderRadius: "12px",
          }}
        >

          <Card.Body>

            {hotelLoading ? (

              <div className="d-flex align-items-center gap-2">

                <Spinner
                  animation="border"
                  size="sm"
                />

                <span className="text-muted">
                  Loading hotel details...
                </span>

              </div>

            ) : hotel ? (

              <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">

                {/* HOTEL DETAILS */}

                <div>

                  <div className="text-muted small mb-1">
                    Adding Room Type For
                  </div>

                  <h4 className="mb-1 fw-semibold">
                    {hotel.name}
                  </h4>

                  <div className="text-muted">

                    {hotel.city}

                    {hotel.location?.name
                      ? ` • ${hotel.location.name}`
                      : ""}

                    {hotel.category
                      ? ` • ${hotel.category}`
                      : ""}

                  </div>

                  {hotel.vendor?.name && (
                    <div className="text-muted small mt-1">
                      Vendor:{" "}
                      <strong>
                        {hotel.vendor.name}
                      </strong>
                    </div>
                  )}

                </div>

                {/* HOTEL SUMMARY */}

                <div className="d-flex align-items-center gap-4">

                  <div className="text-center">

                    <div className="text-muted small">
                      Total Rooms
                    </div>

                    <h5 className="mb-0 fw-semibold">
                      {hotel.totalRoom || 0}
                    </h5>

                  </div>

                  <div className="text-center">

                    <div className="text-muted small">
                      Category
                    </div>

                    <h5 className="mb-0 fw-semibold">
                      {hotel.category || "-"}
                    </h5>

                  </div>

                </div>

                {/* BACK */}

                <button
                  type="button"
                  className="cancel-btn"
                  onClick={() =>
                    navigate(
                      `/admin/hotel-rooms/${id}`
                    )
                  }
                >
                  ← Back to Rooms
                </button>

              </div>

            ) : (

              <div className="text-danger">
                Hotel details could not be loaded.
              </div>

            )}

          </Card.Body>

        </Card>

        {/* =========================
            ROOM FORM
        ========================= */}

        <Card className="p-4 border-0">

          {/* ADD ROOM TYPE BUTTON */}

          <div className="d-flex justify-content-end mt-1">

            <button
              type="button"
              className="my-2 p-2 submit-btn"
              style={{ fontSize: "12px" }}
              onClick={handleAddRoomType}
            >
              <FaPlus size={12} /> Add Room Type
            </button>

          </div>

          <Form onSubmit={handleSubmit}>

            {roomForms.map(
              (room, index) => (

                <div
                  key={index}
                  className="border p-3 mb-3 rounded"
                >

                  {/* ROOM HEADER */}

                  <div className="d-flex justify-content-between mb-2">

                    <h6>
                      Room Type {index + 1}
                    </h6>

                    {index > 0 && (

                      <button
                        type="button"
                        className="btn btn-sm btn-danger"
                        onClick={() =>
                          handleRemoveRoomType(
                            index
                          )
                        }
                      >
                        Remove
                      </button>

                    )}

                  </div>

                  {/* BASIC DETAILS */}

                  <Row className="mb-3">

                    <Col md={4}>

                      <Form.Label>
                        Room Type
                      </Form.Label>

                      <Form.Control
                        name="name"
                        placeholder="Enter room type"
                        value={room.name}
                        onChange={(e) =>
                          handleChange(
                            index,
                            e
                          )
                        }
                        minLength={3}
                        maxLength={50}
                      />

                      {errors.name && (
                        <small className="text-danger">
                          {errors.name}
                        </small>
                      )}

                    </Col>

                    <Col md={4}>

                      <Form.Label>
                        Max Occupancy
                      </Form.Label>

                      <Form.Control
                        type="text"
                        placeholder="Enter max occupancy"
                        name="maxOccupancy"
                        value={
                          room.maxOccupancy
                        }
                        onChange={(e) =>
                          handleLimitedNumberChange(
                            index,
                            "maxOccupancy",
                            e.target.value,
                            MAX_OCCUPANCY
                          )
                        }
                      />

                      {errors.maxOccupancy && (
                        <small className="text-danger">
                          {errors.maxOccupancy}
                        </small>
                      )}

                    </Col>

                    <Col md={4}>

                      <Form.Label>
                        Base Price
                      </Form.Label>

                      <Form.Control
                        type="text"
                        placeholder="Enter base price"
                        name="basePrice"
                        value={
                          room.basePrice
                        }
                        onChange={(e) =>
                          handlePriceChange(
                            index,
                            "basePrice",
                            e.target.value
                          )
                        }
                      />

                      {errors.basePrice && (
                        <small className="text-danger">
                          {errors.basePrice}
                        </small>
                      )}

                    </Col>

                  </Row>

                  {/* EXTRA BED */}

                  <Row className="mb-3">

                    <Col md={3}>

                      <Form.Check
                        label="Extra Bed Allowed"
                        name="extraBedAllowed"
                        checked={
                          room.extraBedAllowed
                        }
                        onChange={(e) =>
                          handleChange(
                            index,
                            e
                          )
                        }
                      />

                    </Col>

                    {room.extraBedAllowed && (
                      <>

                        <Col md={3}>

                          <Form.Label>
                            Extra Bed Price
                          </Form.Label>

                          <Form.Control
                            type="text"
                            placeholder="Enter extra bed price"
                            value={
                              room.extraBedPrice
                            }
                            onChange={(e) =>
                              handlePriceChange(
                                index,
                                "extraBedPrice",
                                e.target.value
                              )
                            }
                          />

                          {errors.extraBedPrice && (
                            <small className="text-danger">
                              {
                                errors.extraBedPrice
                              }
                            </small>
                          )}

                        </Col>

                        <Col md={3}>

                          <Form.Label>
                            Max Extra Beds
                          </Form.Label>

                          <Form.Control
                            type="text"
                            placeholder="Enter max extra beds allowed"
                            value={
                              room.maxExtraBeds
                            }
                            onChange={(e) =>
                              handleLimitedNumberChange(
                                index,
                                "maxExtraBeds",
                                e.target.value,
                                MAX_EXTRA_BEDS
                              )
                            }
                          />

                          {errors.maxExtraBeds && (
                            <small className="text-danger">
                              {
                                errors.maxExtraBeds
                              }
                            </small>
                          )}

                        </Col>

                        <Col md={3}>

                          <Form.Label>
                            Child With Bed Price
                          </Form.Label>

                          <Form.Control
                            type="text"
                            placeholder="Enter child with bed price"
                            value={
                              room.childBedPrice
                            }
                            onChange={(e) =>
                              handlePriceChange(
                                index,
                                "childBedPrice",
                                e.target.value
                              )
                            }
                          />

                          {errors.childBedPrice && (
                            <small className="text-danger">
                              {
                                errors.childBedPrice
                              }
                            </small>
                          )}

                        </Col>

                      </>
                    )}

                  </Row>

                  {/* MEALS */}

                  <h5 className="mt-4 mb-2">
                    Meals
                  </h5>

                  {[
                    "breakfast",
                    "lunch",
                    "dinner",
                  ].map((meal) => (

                    <Row
                      key={meal}
                      className="mb-2 align-items-center"
                    >

                      <Col
                        md={3}
                        className="text-capitalize"
                      >
                        {meal}
                      </Col>

                      <Col md={3}>

                        <Form.Control
                          type="text"
                          placeholder="Enter price"
                          value={
                            room.meals[meal]
                              .price
                          }
                          onChange={(e) =>
                            handlePriceChange(
                              index,
                              "meals",
                              e.target.value,
                              meal
                            )
                          }
                        />

                      </Col>

                      <Col md={3}>

                        <Form.Check
                          label="Included"
                          checked={
                            room.meals[meal]
                              .isIncluded
                          }
                          onChange={(e) =>
                            handleMealChange(
                              index,
                              meal,
                              "isIncluded",
                              e.target.checked
                            )
                          }
                        />

                      </Col>

                      {errors[meal] && (
                        <small className="text-danger">
                          {errors[meal]}
                        </small>
                      )}

                    </Row>

                  ))}

                </div>

              )
            )}

            {/* =========================
                FORM BUTTONS
            ========================= */}

            <Row className="mt-5 mb-5">

              <Col className="d-flex justify-content-center gap-3">

                <button
                  type="button"
                  className="cancel-btn"
                  onClick={() =>
                    navigate(
                      `/admin/hotel-rooms/${id}`
                    )
                  }
                  disabled={loading}
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  className="submit-btn"
                  disabled={
                    loading ||
                    hotelLoading ||
                    !hotel
                  }
                >

                  {loading ? (
                    <>
                      <Spinner
                        animation="border"
                        size="sm"
                        variant="light"
                        className="me-2"
                      />

                      Submitting...
                    </>
                  ) : (
                    "Submit"
                  )}

                </button>

              </Col>

            </Row>

          </Form>

        </Card>

      </Container>
    </>
  );
}